import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Paper,
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import {
  FaUserGraduate,
  FaChalkboardTeacher,
  FaUserTie,
  FaFileInvoice,
  FaWallet,
  FaListAlt,
  FaUniversity,
  FaMoneyBillWave,
  Fa500Px,
  FaPlus,
  FaEye,
  FaBook,
  FaTasks,
  FaBoxOpen,
  FaLayerGroup,
} from "react-icons/fa";
import { MdClass } from "react-icons/md";
import { GiNotebook } from "react-icons/gi";
import api from "../../api";

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const role = localStorage.getItem("role");
  const roleIsSuper = role === "super_admin";

  // Academic Year
  const [sessions, setSessions] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);

  // --------------------------
  //  AUTH CHECK
  // --------------------------
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
  }, [navigate]);

  // --------------------------
  //   FETCH DASHBOARD DATA
  // --------------------------
  const fetchData = async (yearId) => {
    setLoading(true);
    try {
      const res = await api.post("/get-data", {
        id: localStorage.getItem("id"),
        academic_year_id: yearId,
      });

      const resData = res.data?.data || {};

      setData({
        students: Number(resData.students || 0),
        teachers: Number(resData.teachers || 0),
        staff: Number(resData.staff || 0),
        pre_addmission_applicant: Number(resData.total_applicant || 0),
        bill: new Intl.NumberFormat("en-IN").format(resData.bill || 0),
        balance: new Intl.NumberFormat("en-IN").format(resData.balance || 0),
      });
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    }
    setLoading(false);
  };

  // --------------------------
  //   LOAD WHEN YEAR CHANGES
  // --------------------------
  useEffect(() => {
    if (selectedYear) fetchData(selectedYear);
  }, [selectedYear]);

  // --------------------------
  //   FETCH ACADEMIC SESSIONS
  // --------------------------
  const fetchSessions = async () => {
    try {
      const res = await api.get("school/academic_year/list");
      let data = res?.data?.data || [];

      data = data.map((d) => ({
        id: d.id,
        name: d.session_name,
        status: String(d.status).toLowerCase(),
      }));

      const active = data.find((x) => x.status === "active");
      setSelectedYear(active ? active.id : data[0]?.id);

      setSessions(data);
    } catch (err) {
      console.error("Session load failed", err);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  // --------------------------
  // STAT CARDS
  // --------------------------
  const statsCards = [
    {
      id: "students",
      label: "Total Students",
      icon: <FaUserGraduate />,
      color: "#1976D2",
      valueKey: "students",
      routeOnCard: "/admin/view-student",
      routeOnButton: "/admin/add-student",
      buttonLabel: "Add Student",
      buttonIcon: <FaPlus />,
    },
    {
      id: "teachers",
      label: "Total Teachers",
      icon: <FaChalkboardTeacher />,
      color: "#2ECC71",
      valueKey: "teachers",
      routeOnCard: "/admin/teacher-add",
      routeOnButton: "/admin/teacher-add",
      buttonLabel: "Add Teacher",
      buttonIcon: <FaPlus />,
    },
    {
      id: "staff",
      label: "Total Staff",
      icon: <FaUserTie />,
      color: "#F39C12",
      valueKey: "staff",
      routeOnCard: "/admin/dashboard",
      routeOnButton: "/admin/dashboard",
      buttonLabel: "Add Staff",
      buttonIcon: <FaPlus />,
    },
    {
      id: "billing",
      label: "Total Billing",
      icon: <FaFileInvoice />,
      color: "#6C63FF",
      valueKey: "bill",
      routeOnCard: roleIsSuper ? "/admin/all-billing" : "/admin/student-billing",
      routeOnButton: "/admin/student-billing",
      buttonLabel: "New Bill",
      buttonIcon: <FaPlus />,
    },
    {
      id: "wallet",
      label: roleIsSuper ? "Admin Wallet" : "Wallet",
      icon: <FaWallet />,
      color: "#009688",
      valueKey: "balance",
      routeOnCard: roleIsSuper ? "/admin/wallet" : "/admin/staff/wallet",
      routeOnButton: "/admin/wallet-history",
      buttonLabel: "Wallet History",
      buttonIcon: <FaEye />,
    },
    {
      id: "pre_addmission_applicant",
      label: "Total Applicant",
      icon: <FaListAlt />,
      color: "#34495E",
      valueKey: "pre_addmission_applicant",
      routeOnCard: "/admin/applicant/view",
      routeOnButton: "/admin/applicant/view",
      buttonLabel: "View",
      buttonIcon: <FaEye />,
    },
  ];

  const readValue = (key) => {
    if (!data) return "-";
    if (key === "bill" || key === "balance") return `₹ ${data[key]}`;
    return data[key] ?? 0;
  };

  // --------------------------
  // WORKFLOW GROUP
  // --------------------------
  const workflowGroups = [
    {
      title: "Student Registration Process",
      steps: [
        { label: "Add Class & Section", icon: <MdClass />, color: "#1E88E5", to: "/admin/class-section/manage" },
        { label: "Add Fees Type", icon: <FaMoneyBillWave />, color: "#00796B", to: "/admin/fees_structure/list" },
        { label: "Add Student", icon: <FaUserGraduate />, color: "#0288D1", to: "/admin/add-student" },
        { label: "View Student", icon: <GiNotebook />, color: "#3949AB", to: "/admin/view-student" },
      ],
    },
    {
      title: "Teacher Setup Process",
      steps: [
        { label: "Add Department", icon: <FaUniversity />, color: "#2E86C1", to: "/admin/department-add" },
        { label: "Add Teacher", icon: <FaChalkboardTeacher />, color: "#27AE60", to: "/admin/teacher-add" },
        { label: "View Department", icon: <FaUniversity />, color: "#2E86C1", to: "/admin/department-add" },
        { label: "View Teacher", icon: <FaChalkboardTeacher />, color: "#27AE60", to: "/admin/teacher-add" },
      ],
    },
    {
      title: "Subject Assignment Process",
      steps: [
        { label: "Add Subject", icon: <FaBook />, color: "#16A085", to: "/admin/subject-add" },
        { label: "Assign Subject", icon: <FaTasks />, color: "#2ECC71", to: "/admin/subject-class/assign" },
        { label: "Assign Subject To Teacher", icon: <FaUserTie />, color: "#C0392B", to: "/admin/teacher-subject/assign" },
        { label: "View Assign Subject To Teacher", icon: <FaUserTie />, color: "#C0392B", to: "/admin/teacher-subject/assign" },
      ],
    },
    {
      title: "Product Management Process",
      steps: [
        { label: "Add Category", icon: <FaLayerGroup />, color: "#F57C00", to: "/admin/product-category" },
        { label: "Add Sub Category", icon: <FaBoxOpen />, color: "#EF6C00", to: "/admin/product-sub-category" },
        { label: "Add Product", icon: <FaBoxOpen />, color: "#8E24AA", to: "/admin/product-bundle/create" },
        { label: "View Stock", icon: <FaListAlt />, color: "#5E35B1", to: "/admin/product-bundle/list" },
      ],
    },
    {
      title: "Expense Management Process",
      steps: [
        { label: "Add Expense Category", icon: <FaListAlt />, color: "#E53935", to: "/admin/expense/category" },
        { label: "Add Expense Sub Category", icon: <FaLayerGroup />, color: "#D81B60", to: "/admin/expense/sub-category" },
        { label: "Add Vendor", icon: <FaUserTie />, color: "#8E24AA", to: "/admin/vendor/list" },
        { label: "Add Expense", icon: <Fa500Px />, color: "#6A1B9A", to: "/admin/expense/add" },
      ],
    },
  ];

  return (
    <Box
      sx={{
        p: 3,
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #e4ecf5 100%)",
      }}
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 260 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Header + Dropdown */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: 24,
                color: "#1A237E",
              }}
            >
              Dashboard Overview
            </Typography>

            {/* BEAUTIFUL DROPDOWN */}
            <TextField
              select
              label="Academic Year"
              size="small"
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(e.target.value);
                fetchData(e.target.value);
              }}
              SelectProps={{ native: true }}
              sx={{
                width: 250,
                background: "white",
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
            >
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} {s.status === "active" ? "(Active)" : ""}
                </option>
              ))}
            </TextField>
          </Box>

          {/* Stats */}
          <Grid container spacing={3}>
            {statsCards.map((card, index) => (
              <Grid item xs={12} sm={6} md={4} key={card.id}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 2,
                      borderRadius: 3,
                      minHeight: 110,
                      backdropFilter: "blur(10px)",
                      background: "rgba(255, 255, 255, 0.7)",
                      border: "1px solid rgba(255,255,255,0.3)",
                      boxShadow: "0px 8px 20px rgba(0,0,0,0.08)",
                      transition: "all .25s ease",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: "0px 10px 25px rgba(0,0,0,0.15)",
                      },
                      cursor: "pointer",
                    }}
                    onClick={() => navigate(card.routeOnCard)}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        background: `linear-gradient(135deg, ${card.color}33, ${card.color}55)`,
                      }}
                    >
                      <Box sx={{ color: card.color, fontSize: 28 }}>{card.icon}</Box>
                    </Box>

                    <Box sx={{ flex: 1, px: 2 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 14, color: "text.secondary" }}>
                        {card.label}
                      </Typography>
                      <Typography sx={{ fontWeight: 800, fontSize: 22 }}>
                        {readValue(card.valueKey)}
                      </Typography>
                    </Box>

                    <Button
                      variant="contained"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(card.routeOnButton);
                      }}
                      startIcon={card.buttonIcon}
                      sx={{
                        background: `linear-gradient(90deg, ${card.color}, ${card.color}cc)`,
                        color: "white",
                        fontWeight: 700,
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.8,
                      }}
                    >
                      {card.buttonLabel}
                    </Button>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 6, opacity: 0.3 }} />

          {/* ⚙️ Workflow Groups */}
          {workflowGroups.map((group, gIndex) => (
            <Box key={gIndex} sx={{ mb: 6 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  color: "#37474F",
                }}
              >
                {group.title}
              </Typography>
              <Grid container spacing={3}>
                {group.steps.map((step, index) => (
                  <Grid item xs={12} sm={6} md={3} key={step.label}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                    <Paper
                        elevation={0}
                      sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                        p: 3,
                        borderRadius: 3,
                          backdropFilter: "blur(10px)",
                          background: "rgba(255,255,255,0.75)",
                          border: "1px solid rgba(255,255,255,0.3)",
                          textAlign: "center",
                          transition: "all .25s ease",
                        "&:hover": {
                          transform: "translateY(-6px)",
                            boxShadow: "0px 5px 20px rgba(0,0,0,0.15)",
                        },
                          cursor: "pointer",
                      }}
                      onClick={() => navigate(step.to)}
                    >
                      <Box
                        sx={{
                          width: 70,
                          height: 70,
                          borderRadius: "50%",
                          display: "grid",
                          placeItems: "center",
                            background: `linear-gradient(135deg, ${step.color}33, ${step.color}55)`,
                          mb: 1.5,
                        }}
                      >
                          <Box sx={{ color: step.color, fontSize: 30 }}>{step.icon}</Box>
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#2C3E50" }}>
                        {step.label}
                      </Typography>
                    </Paper>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </>
      )}
    </Box>
  );
};

export default Dashboard;
